import usePayWall from "@/hooks/usePaywall";
import useSubscriptionPermission from "@/hooks/useSubscriptionPermission";
import Ad from "@/src/ads/components/Ad";
import clsx from "clsx";
import TimerButton from "components/TimerButton";
import TimerDisplay from "components/TimerDisplay";
import Video from "components/Video";
import { RecipeContext } from "contexts/RecipeContext";
import { AnimatePresence, motion } from "framer-motion";
import { useStores } from "hooks/useStores";
import { transitionDefaults } from "lib/constants";
import { observer } from "mobx-react";
import { useContext, useEffect, useRef, useState } from "react";
import { Check, Play, X } from "react-feather";
import { useWindowSize } from "react-use";
import useGTM from "@/hooks/useGTM";

const Step = observer(
  ({
    step,
    number,
    index,
    isRecipeActive,
    isLastStep,
    isActive,
    isCompleted,
    recipe,
  }) => {
    const { authStore, timerStore } = useStores();
    const ref = useRef(null);
    const { subscriptionPermission } = useSubscriptionPermission(recipe);
    const { setPaywall } = usePayWall();
    const gtm = useGTM();

    const [showVideo, setShowVideo] = useState(index === 0);

    const {
      setActiveStepIndex,
      setActiveRecipe,
      activeRecipe,
      currentlyPlayingVideoIndex,
      setCurrentlyPlayingVideoIndex,
    } = useContext(RecipeContext);

    const handleUncompleted = () => {
      setActiveStepIndex(index);
    };

    const handleNext = () => {
      setActiveStepIndex(index + 1);
    };

    const handleFinish = () => {
      setActiveStepIndex(index + 1);
      gtm.pushEvent("recipe_completed", {
        recipeId: recipe.id,
        recipeTitle: recipe.title,
      });
      setActiveRecipe(null);
    };

    const paywallSettings = {
      isOpen: true,
      isClosable: true,
      title: recipe.title,
      variant: "stepByStep",
    };

    const handleToggleShow = () => {
      if (
        index === stepWithFirstVideo ||
        (authStore.currentUser && subscriptionPermission.canViewMethodVideos)
      ) {
        if (!showVideo) setCurrentlyPlayingVideoIndex(index);

        setShowVideo(!showVideo);
      } else {
        gtm.pushEvent("paywall_hit", {
          type: "method_video",
          recipeId: recipe.id,
          recipeTitle: recipe.title,
          stepNumber: number,
        });
        setPaywall(paywallSettings);
      }
    };

    const isVideoPlaying = currentlyPlayingVideoIndex === index;

    const timer =
      activeRecipe && timerStore.timers.length > 0
        ? timerStore.getStepTimer(activeRecipe.id, step.id)
        : null;

    const stepWithFirstVideo =
      recipe.method.findIndex((step) => step.brightcoveVideoId) ?? 0;

    return (
      <div
        className={clsx("Step", "transition duration-300 ease-out", {
          "opacity-25": isRecipeActive && !isActive && !isCompleted,
          "opacity-100": (isRecipeActive && isActive) || isCompleted,
          "pointer-events-none": isRecipeActive && !isCompleted && !isActive,
        })}
        ref={ref}
      >
        <div
          className={clsx(
            "Step__header",
            "mb-2 flex items-center border-b pb-2"
          )}
        >
          <h3 className={clsx("Step__heading", "heading-xs tabular-nums")}>
            Step {number}. {step.heading}
          </h3>
          {!!step.brightcoveVideoId && (
            <button
              onClick={handleToggleShow}
              className={clsx(
                "Step__toggleVideoButton",
                "group ml-auto flex items-center space-x-2"
              )}
            >
              <div
                className={clsx(
                  "text-base text-zinc-500 group-hover:text-zinc-900",
                  {
                    block: index === 0,
                    hidden: index > 0,
                  }
                )}
              >
                {showVideo ? "Hide" : "Watch now"}
              </div>
              <div
                className={clsx(
                  "flex h-7 w-11 items-center justify-center rounded-2xl",
                  {
                    "bg-zinc-800 hover:bg-zinc-800": !showVideo,
                    "bg-zinc-300 hover:bg-zinc-800": showVideo,
                  }
                )}
              >
                {!showVideo && (
                  <Play
                    className={clsx(
                      "relative left-[1px] h-3 fill-white stroke-white"
                    )}
                  />
                )}
                {showVideo && (
                  <X
                    className={clsx(
                      "h-4 stroke-black group-hover:stroke-white"
                    )}
                  />
                )}
              </div>
            </button>
          )}
        </div>

        <div className={clsx("flex flex-col-reverse sm:flex-row")}>
          <div
            className={clsx(
              "Step__description",
              "body-text sm:body-text-sm lg:body-text max-w-prose [&_p:last-child]:mb-0",
              {
                "w-full sm:w-1/2 sm:pr-8":
                  !!step.brightcoveVideoId && showVideo,
                "w-full": !step.brightcoveVideoId || !showVideo,
              }
            )}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: step.description }}
          />
          {!!step.brightcoveVideoId && (
            <AnimatePresence exitBeforeEnter>
              {showVideo && (
                <div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={transitionDefaults}
                  className={clsx(
                    "Step__video",
                    "mx-auto my-4 w-3/5 sm:mx-0 sm:my-0 sm:w-1/2"
                  )}
                >
                  <div
                    className={clsx(
                      "relative aspect-[9/16] w-full [&>div>video-js]:h-full [&>div>video-js]:w-full"
                    )}
                  >
                    <Video
                      brightcoveVideoId={step.brightcoveVideoId}
                      className={clsx("h-full w-full")}
                      playing={isVideoPlaying}
                      onPlay={() => {
                        setCurrentlyPlayingVideoIndex(index);
                      }}
                      hideControls
                      showProgressBar
                      loop
                      muted
                      hasPermissionToView={
                        // it's the first step with a video
                        index === stepWithFirstVideo ||
                        (authStore.currentUser &&
                          subscriptionPermission.canViewMethodVideos)
                      }
                      paywallSettings={paywallSettings}
                    />
                  </div>
                </div>
              )}
            </AnimatePresence>
          )}
        </div>

        {isRecipeActive && (
          <div className={clsx("Step__actions", "flex items-center pt-4")}>
            <div className="Step__action">
              <Action
                isActive={isActive}
                isCompleted={isCompleted}
                isLastStep={isLastStep}
                handleNext={handleNext}
                handleFinish={handleFinish}
                handleUncompleted={handleUncompleted}
                number={number}
              />
            </div>
            {step.time && (
              <div
                className={clsx("Step__action Step__action--timer", "ml-auto")}
              >
                {!timer && (
                  <div className="Step__addTimer">
                    <TimerButton
                      recipeId={activeRecipe.id}
                      stepId={step.id}
                      heading={step.heading ? step.heading : `Step ${number}`}
                      minutes={step.time}
                      isDisabled={!isActive}
                    />
                  </div>
                )}
                {timer && (
                  <div className="Step__timer">
                    <TimerDisplay timer={timer} isLarge />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {number % 5 === 0 && (
          <Ad
            placementName={
              process.env.NEXT_PUBLIC_ADS_PROVIDER === "freestar"
                ? `mob.co.uk_square_${number / 5}`
                : `htlad-recipe_method`
            }
          />
        )}
      </div>
    );
  }
);

const Action = observer(
  ({
    isActive,
    isCompleted,
    isLastStep,
    handleNext,
    handleFinish,
    handleUncompleted,
    number,
  }) => {
    const { width: windowWidth } = useWindowSize();

    const { activeRecipe } = useContext(RecipeContext);

    const [isHovering, setIsHovering] = useState(false);
    const [showIcon, setShowIcon] = useState(isCompleted);

    const handleClick = () => {
      setShowIcon(!isCompleted);

      if (!isLastStep) {
        setTimeout(handleNext, 750);
      }

      if (isLastStep) {
        setTimeout(handleFinish, 750);
      }

      if (isCompleted) {
        setTimeout(handleUncompleted, 750);
      }
    };

    const handleMouseEnter = () => {
      if (windowWidth < 768) setIsHovering(true);
    };

    const handleMouseLeave = () => {
      if (windowWidth < 768) setIsHovering(false);
    };

    // If a step is undone, we need to reset the showIcon state
    useEffect(() => {
      if (showIcon && !isCompleted && !isActive) setShowIcon(false);
    }, [showIcon, isCompleted, isActive]);

    const label = isLastStep ? "Finish" : "Next";

    return (
      <motion.button
        className={clsx(
          "Step__button",
          "flex h-12 w-20 items-center justify-center overflow-hidden border-2 border-black text-lg focus:border-black lg:h-14 lg:w-24"
        )}
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        transition={transitionDefaults}
        data-recipe-id={activeRecipe.id}
        data-recipe-title={activeRecipe.title}
        data-step-number={number}
        aria-label={label}
      >
        <AnimatePresence exitBeforeEnter>
          {!showIcon && (
            <motion.div
              key="label"
              className="Step__buttonLabel"
              initial={{ x: "-200%" }}
              animate={{ x: "0%" }}
              exit={{ x: "200%" }}
              layout
              transition={transitionDefaults}
            >
              {label}
            </motion.div>
          )}
          {showIcon && (
            <motion.div
              key="icon"
              className="Step__buttonIcon"
              initial={{ x: "-200%" }}
              animate={{ x: "0%" }}
              exit={{ x: "200%" }}
              layout
              transition={transitionDefaults}
              onAnimationComplete={handleMouseLeave}
            >
              {isHovering && !isActive ? (
                <X size={21} strokeWidth={2} />
              ) : (
                <Check size={21} strokeWidth={2} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    );
  }
);

export default Step;
